import axios from 'axios';
import { useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2, Download, RotateCcw, Pencil, X, Building2, Save, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InvoicesTab = ({
  invoices,
  invoiceMonth, setInvoiceMonth,
  invoiceYear, setInvoiceYear,
  generateMonthlyInvoices, generatingInvoices,
  getAuthHeader,
  refreshInvoices,
}) => {
  const [backfilling, setBackfilling] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');

  const backfillInvoices = async () => {
    if (!window.confirm('Générer les factures manquantes pour toutes les courses déjà terminées ?')) return;
    setBackfilling(true);
    try {
      const r = await axios.post(`${API}/admin/invoices/backfill`, null, { headers: getAuthHeader() });
      toast.success(r.data.message || 'Factures générées');
      if (refreshInvoices) refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du rattrapage');
    } finally {
      setBackfilling(false);
    }
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      const r = await axios.get(`${API}/admin/invoices/booking/${invoice.booking_id}/pdf`, { headers: getAuthHeader(), responseType: 'blob' });
      const u = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = u;
      a.download = `facture-${invoice.booking_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(u);
      toast.success('Facture telechargee');
    } catch (e) { toast.error('Erreur telechargement facture'); }
  };

  const openEdit = (invoice) => {
    setEditingInvoice({
      ...invoice,
      is_professional: !!invoice.is_professional,
      company_name: invoice.company_name || '',
      company_address: invoice.company_address || '',
      company_nif: invoice.company_nif || '',
      company_vat: invoice.company_vat || '',
      client_name: invoice.client_name || '',
      client_phone: invoice.client_phone || '',
      client_email: invoice.client_email || '',
    });
    setEmailRecipient(invoice.client_email || '');
    // Only pre-fill phone if it looks like a real number (≥ 9 digits). Avoid carrying over "+2" / empty.
    const rawPhone = (invoice.client_phone || '').trim();
    const digitsCount = (rawPhone.match(/\d/g) || []).length;
    setWhatsappPhone(digitsCount >= 9 ? rawPhone : '');
  };

  const sendInvoiceEmail = async () => {
    if (!editingInvoice) return;
    const to = (emailRecipient || '').trim();
    if (!to || !to.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    if (!window.confirm(`Envoyer la facture ${editingInvoice.invoice_number} à ${to} ?`)) return;
    setSendingEmail(true);
    try {
      // Save any pending edits first so the PDF uses the latest data
      const payload = {
        client_name: editingInvoice.client_name,
        client_phone: editingInvoice.client_phone,
        client_email: editingInvoice.client_email,
        is_professional: editingInvoice.is_professional,
        company_name: editingInvoice.company_name,
        company_address: editingInvoice.company_address,
        company_nif: editingInvoice.company_nif,
        company_vat: editingInvoice.company_vat,
      };
      try {
        await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, payload, { headers: getAuthHeader() });
      } catch (_) { /* non-blocking: send even if update fails */ }
      const r = await axios.post(`${API}/admin/invoices/${editingInvoice.id}/send-email`, { to_email: to }, { headers: getAuthHeader() });
      toast.success(r.data.message || `Facture envoyée à ${to}`);
      if (refreshInvoices) refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendInvoiceWhatsapp = async () => {
    if (!editingInvoice) return;
    const phone = (whatsappPhone || '').trim();
    const digitsCount = (phone.match(/\d/g) || []).length;
    if (!phone || digitsCount < 9) {
      toast.error(`Numéro trop court (${digitsCount} chiffres). Format : 06xxxxxxxx ou +336xxxxxxxx.`);
      return;
    }
    setSendingWhatsapp(true);
    try {
      // Save any pending edits first so the PDF uses the latest data
      const payload = {
        client_name: editingInvoice.client_name,
        client_phone: editingInvoice.client_phone,
        client_email: editingInvoice.client_email,
        is_professional: editingInvoice.is_professional,
        company_name: editingInvoice.company_name,
        company_address: editingInvoice.company_address,
        company_nif: editingInvoice.company_nif,
        company_vat: editingInvoice.company_vat,
      };
      try {
        await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, payload, { headers: getAuthHeader() });
      } catch (_) { /* non-blocking */ }

      const r = await axios.post(
        `${API}/admin/invoices/${editingInvoice.id}/whatsapp-link`,
        { phone },
        { headers: getAuthHeader() }
      );
      const { phone_wa, pdf_path, invoice_number, total_ttc } = r.data;
      const pdf_url = `${process.env.REACT_APP_BACKEND_URL}${pdf_path}`;
      const ttcStr = (typeof total_ttc === 'number') ? `${total_ttc.toFixed(2)} EUR TTC` : '';
      const message = `Bonjour, voici votre facture YOUMI GREEN VTC ${invoice_number || ''}${ttcStr ? ` (${ttcStr})` : ''}.\nTélécharger la facture : ${pdf_url}\n\nMerci de votre confiance !`;
      const waLink = `https://wa.me/${phone_wa}?text=${encodeURIComponent(message)}`;
      const w = window.open(waLink, '_blank', 'noopener,noreferrer');
      if (!w) {
        // Popup blocked: copy link to clipboard as fallback
        try { await navigator.clipboard.writeText(waLink); } catch (_) {}
        toast.success('Lien WhatsApp copié (popup bloqué)');
      } else {
        toast.success('WhatsApp ouvert');
      }
      if (refreshInvoices) refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la préparation du lien WhatsApp");
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const saveInvoiceEdit = async () => {
    if (!editingInvoice) return;
    if (editingInvoice.is_professional && (!editingInvoice.company_name.trim() || !editingInvoice.company_address.trim())) {
      toast.error("Nom et adresse de l'entreprise requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: editingInvoice.client_name,
        client_phone: editingInvoice.client_phone,
        client_email: editingInvoice.client_email,
        is_professional: editingInvoice.is_professional,
        company_name: editingInvoice.company_name,
        company_address: editingInvoice.company_address,
        company_nif: editingInvoice.company_nif,
        company_vat: editingInvoice.company_vat,
      };
      await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, payload, { headers: getAuthHeader() });
      toast.success('Facture mise à jour');
      setEditingInvoice(null);
      if (refreshInvoices) refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Générer les factures mensuelles</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Mois</label>
            <select
              value={invoiceMonth}
              onChange={(e) => setInvoiceMonth(parseInt(e.target.value))}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i).toLocaleDateString('fr-FR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Année</label>
            <select
              value={invoiceYear}
              onChange={(e) => setInvoiceYear(parseInt(e.target.value))}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <Button
            onClick={generateMonthlyInvoices}
            disabled={generatingInvoices}
            className="btn-primary"
            data-testid="generate-invoices-btn"
          >
            {generatingInvoices ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Génération...</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Générer les factures</>
            )}
          </Button>
        </div>
        <p className="text-zinc-500 text-sm mt-3">
          Génère les factures pour tous les comptes professionnels avec facturation mensuelle.
        </p>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white font-['Outfit'] mb-2">Rattrapage des factures manquantes</h3>
        <p className="text-zinc-400 text-sm mb-4">
          Crée une facture pour chaque course déjà terminée qui n'en a pas. À utiliser si des courses ont été
          terminées avant l'activation de la génération automatique.
        </p>
        <Button
          onClick={backfillInvoices}
          disabled={backfilling}
          variant="outline"
          className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
          data-testid="backfill-invoices-btn"
        >
          {backfilling ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Génération...</>
          ) : (
            <><RotateCcw className="w-4 h-4 mr-2" /> Générer les factures manquantes</>
          )}
        </Button>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-800/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">N° Facture</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Trajet / Periode</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">HT</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">TVA 10%</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">TTC</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {invoices.map(invoice => (
                <tr key={invoice.id} className="hover:bg-zinc-800/30">
                  <td className="px-4 py-3 text-white font-mono text-xs">{invoice.invoice_number}</td>
                  <td className="px-4 py-3">
                    {invoice.is_professional && invoice.company_name ? (
                      <div>
                        <div className="text-amber-300 text-sm font-semibold flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {invoice.company_name}
                        </div>
                        <div className="text-zinc-500 text-xs">{invoice.client_name}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-white text-sm">{invoice.client_name || invoice.company_name || '-'}</div>
                        {invoice.client_phone && <div className="text-zinc-500 text-xs">{invoice.client_phone}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">
                    {invoice.pickup_address ? (
                      <div>
                        <div>{invoice.pickup_address?.split(',')[0]}</div>
                        <div className="text-zinc-500">→ {invoice.destination_address?.split(',')[0]}</div>
                        <div className="text-zinc-600">{invoice.pickup_date} {invoice.pickup_time}</div>
                      </div>
                    ) : invoice.month ? (
                      new Date(invoice.year, invoice.month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-white text-sm">{(invoice.total_ht || 0).toFixed(2)}€</td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{(invoice.total_tva || 0).toFixed(2)}€</td>
                  <td className="px-4 py-3 text-emerald-500 font-bold text-sm">{(invoice.total_ttc || 0).toFixed(2)}€</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {invoice.booking_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(invoice)}
                          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 text-xs h-7"
                          title="Modifier la facture (infos client / société)"
                          data-testid={`edit-invoice-${invoice.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {invoice.booking_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoicePdf(invoice)}
                          className="border-zinc-700 text-zinc-300 text-xs h-7"
                          data-testid={`download-invoice-${invoice.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {invoices.length === 0 && (
          <div className="p-8 text-center text-zinc-500">
            Aucune facture generee. Les factures sont creees automatiquement quand une course est terminee.
          </div>
        )}
      </div>

      {/* Edit Invoice Modal */}
      {editingInvoice && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingInvoice(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Modifier la facture</h3>
                <p className="text-zinc-500 text-xs font-mono">{editingInvoice.invoice_number}</p>
              </div>
              <button onClick={() => setEditingInvoice(null)} className="text-zinc-400 hover:text-white" data-testid="close-edit-invoice">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Client section */}
              <div className="space-y-3">
                <div className="text-zinc-300 text-sm font-semibold">Client</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Nom complet</label>
                    <Input
                      value={editingInvoice.client_name}
                      onChange={(e) => setEditingInvoice({ ...editingInvoice, client_name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                      data-testid="edit-client-name"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Téléphone</label>
                    <Input
                      value={editingInvoice.client_phone}
                      onChange={(e) => setEditingInvoice({ ...editingInvoice, client_phone: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                      data-testid="edit-client-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs block mb-1">Email</label>
                  <Input
                    value={editingInvoice.client_email}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, client_email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    data-testid="edit-client-email"
                  />
                </div>
              </div>

              {/* B2B section */}
              <div className="p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingInvoice.is_professional}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, is_professional: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-amber-500"
                    data-testid="edit-professional-toggle"
                  />
                  <div>
                    <div className="text-amber-200 text-sm font-semibold flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Facture au nom d'une entreprise
                    </div>
                    <div className="text-amber-400/70 text-xs mt-0.5">
                      Active si le client demande une facture pour sa société.
                    </div>
                  </div>
                </label>
                {editingInvoice.is_professional && (
                  <div className="space-y-2 pl-6">
                    <div>
                      <label className="text-amber-300 text-xs block mb-1">Nom de l'entreprise *</label>
                      <Input
                        value={editingInvoice.company_name}
                        onChange={(e) => setEditingInvoice({ ...editingInvoice, company_name: e.target.value })}
                        placeholder="Ex: ACME Sarl"
                        className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                        data-testid="edit-company-name"
                      />
                    </div>
                    <div>
                      <label className="text-amber-300 text-xs block mb-1">Adresse complète *</label>
                      <textarea
                        value={editingInvoice.company_address}
                        onChange={(e) => setEditingInvoice({ ...editingInvoice, company_address: e.target.value })}
                        placeholder="Numéro, rue, code postal, ville, pays"
                        rows={2}
                        className="w-full px-3 py-2 bg-zinc-900 border border-amber-500/40 rounded-lg text-amber-100 text-sm resize-none"
                        data-testid="edit-company-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-amber-300 text-xs block mb-1">NIF (optionnel)</label>
                        <Input
                          value={editingInvoice.company_nif}
                          onChange={(e) => setEditingInvoice({ ...editingInvoice, company_nif: e.target.value })}
                          placeholder="N° NIF"
                          className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                          data-testid="edit-company-nif"
                        />
                      </div>
                      <div>
                        <label className="text-amber-300 text-xs block mb-1">N° TVA Intra (optionnel)</label>
                        <Input
                          value={editingInvoice.company_vat}
                          onChange={(e) => setEditingInvoice({ ...editingInvoice, company_vat: e.target.value })}
                          placeholder="Ex: FR12345678901"
                          className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                          data-testid="edit-company-vat"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Send by email block (manual only) */}
              <div className="p-3 bg-blue-500/5 border border-blue-500/30 rounded-lg space-y-2">
                <div className="text-blue-200 text-sm font-semibold flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Envoyer la facture par email
                </div>
                <div className="text-blue-400/70 text-xs">
                  Envoi manuel uniquement. Le PDF de la facture sera joint à l'email.
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    placeholder="email@destinataire.com"
                    className="bg-zinc-900 border-blue-500/40 text-blue-100 text-sm flex-1"
                    data-testid="invoice-email-recipient"
                  />
                  <Button
                    onClick={sendInvoiceEmail}
                    disabled={sendingEmail || !emailRecipient}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="send-invoice-email-btn"
                  >
                    {sendingEmail ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Envoi...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-1" /> Envoyer</>
                    )}
                  </Button>
                </div>
                {editingInvoice.last_sent_at && (
                  <div className="text-blue-300/60 text-xs">
                    Dernier envoi : {new Date(editingInvoice.last_sent_at).toLocaleString('fr-FR')} → {editingInvoice.last_sent_to}
                  </div>
                )}
              </div>

              {/* Send by WhatsApp block (manual only) */}
              <div className="p-3 bg-emerald-500/5 border border-emerald-500/30 rounded-lg space-y-2">
                <div className="text-emerald-200 text-sm font-semibold flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" /> Envoyer la facture par WhatsApp
                </div>
                <div className="text-emerald-400/70 text-xs">
                  Envoi manuel uniquement. WhatsApp s'ouvre avec un message pré-rempli contenant un lien sécurisé vers le PDF.
                  <br />Format accepté : <code>06xxxxxxxx</code> ou <code>+336xxxxxxxx</code> (9 chiffres minimum).
                </div>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    value={whatsappPhone}
                    onChange={(e) => setWhatsappPhone(e.target.value)}
                    placeholder="06... ou +33..."
                    className="bg-zinc-900 border-emerald-500/40 text-emerald-100 text-sm flex-1"
                    data-testid="invoice-whatsapp-phone"
                  />
                  <Button
                    onClick={sendInvoiceWhatsapp}
                    disabled={sendingWhatsapp || !whatsappPhone}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="send-invoice-whatsapp-btn"
                  >
                    {sendingWhatsapp ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-1" /> ...</>
                    ) : (
                      <><MessageCircle className="w-4 h-4 mr-1" /> Ouvrir WhatsApp</>
                    )}
                  </Button>
                </div>
                {editingInvoice.last_whatsapp_sent_at && (
                  <div className="text-emerald-300/60 text-xs">
                    Dernier partage : {new Date(editingInvoice.last_whatsapp_sent_at).toLocaleString('fr-FR')} → {editingInvoice.last_whatsapp_sent_to}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={saveInvoiceEdit}
                  disabled={saving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-invoice-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Enregistrement...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Enregistrer</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditingInvoice(null)}
                  className="border-zinc-700 text-zinc-300"
                  data-testid="cancel-edit-invoice"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
